import type { SiteConfigFn } from '@wix/thunderbolt-vsm-api-types'
import type { TPAPublicClientSpecEntry, TPAPublicClientWidgetEntry } from '@wix/thunderbolt-ssr-api'
import type { CustomElementWidget, WixCustomElementComponentSiteConfig } from '../types'

const CUSTOM_ELEMENT = 'wixui.CustomElementComponent'

const isCustomElementWidget = (widget: TPAPublicClientWidgetEntry): boolean => {
	const componentFields = widget.componentFields || {}
	const scriptUrl = componentFields.scriptUrl
	const tagName = componentFields.tagName
	const scriptType = componentFields.scriptType as string | undefined

	return (
		typeof scriptUrl === 'string' &&
		typeof tagName === 'string' &&
		(scriptType === undefined || scriptType === 'NONE' || scriptType === 'ES_MODULE' || scriptType === 'COMMON_JS')
	)
}

const wixCustomElementComponentSiteConfig: SiteConfigFn<WixCustomElementComponentSiteConfig> = ({ api }) => {
	const componentsCount = api.rendererModel.getSiteMetaData()?.renderHints?.componentsCount
	const siteHasCustomElements = componentsCount ? !!componentsCount[CUSTOM_ELEMENT] : true

	const premiumFeatures = api.rendererModel.getPremiumFeatures() || []
	const isWixSite = api.rendererModel.getSiteInfo()?.documentType === 'WixSite'
	const isPremiumDomain = premiumFeatures.includes('HasDomain')
	const isAdFreePremiumUser = premiumFeatures.includes('AdsFree')
	const domain = api.publicModel.getDomain()
	const isFreeWixDomain = domain === 'wixsite.com' || domain === 'editorx.io'

	const experiments = api.env.getExperiments()
	const shouldLoadAllExternalScripts =
		!!experiments['bv_allow_loading_external_scripts'] ||
		isWixSite ||
		(isPremiumDomain && isAdFreePremiumUser && !isFreeWixDomain)

	const clientSpecMap = api.rendererModel.getClientSpecMap() || {}
	const csmEntries = Object.values(clientSpecMap) as Array<TPAPublicClientSpecEntry>

	const widgetsToRenderOnFreeSites: Record<string, boolean> = {}
	const customElementWidgets: Record<string, CustomElementWidget> = {}
	const studioWidgetsToRender: Record<string, boolean> = {}

	for (const app of csmEntries) {
		const widgets = app.widgets || {}
		const appDefinitionId = app.appDefinitionId

		for (const [widgetId, widget] of Object.entries(widgets)) {
			if (widget.componentFields?.allowedForFreeSite) {
				widgetsToRenderOnFreeSites[widgetId] = true
			}

			if (isCustomElementWidget(widget)) {
				widgetsToRenderOnFreeSites[widgetId] = true
				const key = `${appDefinitionId}-${widgetId}`
				customElementWidgets[key] = {
					scriptUrl: widget.componentFields!.scriptUrl as string,
					tagName: widget.componentFields!.tagName as string,
					scriptType: widget.componentFields!.scriptType as CustomElementWidget['scriptType'],
				}
			}

			if (widget.componentFields?.appStudioFields) {
				const studioId = (widget.componentFields.appStudioFields as any).id
				const studioKey = `${appDefinitionId}-${studioId}`
				studioWidgetsToRender[studioKey] = true
			}
		}
	}

	const allWidgetsToRenderOnFreeSites = { ...widgetsToRenderOnFreeSites, ...studioWidgetsToRender }

	const shouldLoad = siteHasCustomElements || Object.keys(studioWidgetsToRender).length > 0

	if (!shouldLoad) {
		return {
			shouldLoadSiteFeature: false,
			shouldLoadSiteConfig: false,
			siteConfig: null,
		}
	}

	const wixCodeBundlersUrlData = api.platform.getWixCodeBundlersUrlData()

	return {
		shouldLoadSiteFeature: true,
		shouldLoadSiteConfig: true,
		siteConfig: {
			shouldLoadAllExternalScripts,
			widgetsToRenderOnFreeSites: allWidgetsToRenderOnFreeSites,
			wixCodeBundlersUrlData,
			customElementWidgets,
		},
	}
}

const name = 'wixCustomElementComponent'

export const config = { site: wixCustomElementComponentSiteConfig, name }
